import type { PaletteOptions as PaletteColors } from '@mui/material';

const SlabGray = {
  0: '#FFFFFF',
  50: '#F9FAFC',
  100: '#F5F5F5',
  300: '#E5E7EB',
  400: '#C5C6CA',
  500: '#A1A2AA',
  600: '#727382',
  800: '#091747',
  900: '#061136',
  950: '#050E2C',
  Stroke: '#EAEDF9',
  Table: '#F3F4F6',
  TableHover: '#F8FAFF',
  Border: '#E0E0E0',
} as const;

const SlabYellow = {
  100: '#FFE9D9',
  200: '#FFF3D4',
  300: '#FFE7A8',
  400: '#FFDE80',
  500: '#FFD641',
  600: '#FFC800',
  700: '#E6C13A',
  800: '#FF7B01',
} as const;

const SlabBrown = {
  200: '#F5EAE1',
  400: '#E8B84E',
  500: '#CD930D',
  600: '#A76E1D',
  800: '#744D14',
} as const;

const SlabGreen = {
  50: '#F9FCFA',
  100: '#EBFFF4',
  200: '#DFECE7',
  300: '#4CAF50',
  400: '#3FC79A',
  500: '#0DA289',
  600: '#0B815A',
  800: '#075A3E',
  Status: '#1EB649',
} as const;

const SlabRed = {
  100: '#FFEBF0',
  200: '#F9E0E4',
  400: '#FF7B79',
  500: '#EE1D52',
  600: '#C61834',
  800: '#8A0F24',
} as const;

const SlabCherry = {
  100: '#FFEAE7',
  600: '#D51C00',
} as const;

const SlabTurquoise = {
  200: '#EFEFEF',
  500: '#00B4D8',
  600: '#0077B6',
  800: '#023E8A',
  900: '#03045E',
} as const;

const SlabBlue = {
  100: '#E3F2FD',
  200: '#BBDEFB',
  400: '#42A5F5',
  500: '#2196F3',
  600: '#1E88E5',
  800: '#1565C0',
  Cyan: '#32ADE6',
  Selected: '#E5EEFA',
  IceLight: '#E4F7FB',
} as const;

const SlabIndigo = {
  50: '#EEF4FC',
  100: '#E8EDFF',
  200: '#DCDFE3',
  300: '#CDD5DF',
  500: '#6B8AFC',
  600: '#6B8AFC',
  700: '#3E5CC6',
} as const;

const SlabPurple = {
  100: '#7468E4',
  200: '#EFECFD',
  600: '#7468E4',
  800: '#9C27B0',
};

const SlabStatus = {
  error: {
    ...SlabRed,
    light: SlabRed['400'],
    main: SlabRed['600'],
    dark: SlabRed['800'],
  },
  warning: {
    ...SlabBrown,
    light: SlabBrown['400'],
    main: SlabBrown['600'],
    dark: SlabBrown['800'],
  },
  success: {
    ...SlabGreen,
    light: SlabGreen['400'],
    main: SlabGreen['600'],
    dark: SlabGreen['800'],
  },
  info: {
    ...SlabTurquoise,
    light: SlabTurquoise['500'],
    main: SlabTurquoise['600'],
    dark: SlabTurquoise['800'],
  },
} as const;

declare module '@mui/material/styles' {
  interface Palette {
    SlabGray: typeof SlabGray;
    SlabYellow: typeof SlabYellow;
    SlabBrown: typeof SlabBrown;
    SlabRed: typeof SlabRed;
    SlabCherry: typeof SlabCherry;
    SlabGreen: typeof SlabGreen;
    SlabTurquoise: typeof SlabTurquoise;
    SlabBlue: typeof SlabBlue;
    SlabIndigo: typeof SlabIndigo;
    SlabPurple: typeof SlabPurple;
    SlabStatus: typeof SlabStatus;
  }
  interface PaletteOptions {
    SlabGray: typeof SlabGray;
    SlabYellow: typeof SlabYellow;
    SlabBrown: typeof SlabBrown;
    SlabRed: typeof SlabRed;
    SlabCherry: typeof SlabCherry;
    SlabGreen: typeof SlabGreen;
    SlabTurquoise: typeof SlabTurquoise;
    SlabBlue: typeof SlabBlue;
    SlabIndigo: typeof SlabIndigo;
    SlabPurple: typeof SlabPurple;
    SlabStatus: typeof SlabStatus;
  }
}

export const palette: PaletteColors = {
  success: SlabStatus.success,
  warning: SlabStatus.warning,
  error: SlabStatus.error,
  SlabGray,
  SlabYellow,
  SlabBrown,
  SlabRed,
  SlabCherry,
  SlabGreen,
  SlabTurquoise,
  SlabBlue,
  SlabIndigo,
  SlabPurple,
  SlabStatus,
};
